<script>
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import Swal from "sweetalert2";
import { useSharedStore } from "@/stores/shared-store";
import { useUserStore } from "@/stores/user/user-store";
import { useLearningPathStore } from "@/stores/learning-path-store";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store";

export default {
  components: { BaseLoading },

  data() {
    return {
      sharedStore: useSharedStore(),
      userStore: useUserStore(),
      learningPathStore: useLearningPathStore(),
      courseFetchStore: useCourseFetchStore(),

      // Modal Create
      showCreateModal: false,
      newPath: { title: "", note: "" },
      newPathItems: [], // [{ course_id, course_title, order }]
      selectedCourseId: "",

      // Modal Edit
      showEditModal: false,
      editingPathId: null,
      editPath: { title: "", note: "" },
      editPathItems: [], // [{ course_id, course_title, order }]
      selectedEditCourseId: "",

      // Modal Assign
      showAssignModal: false,
      selectedPath: null,
      selectedStudentIds: [],
      studentSearchQuery: "",
    };
  },

  computed: {
    // Course yang belum ditambahkan ke newPathItems
    availableCourses() {
      const addedIds = this.newPathItems.map((i) => i.course_id);
      return this.courseFetchStore.courseCreatedList.filter(
        (c) => !addedIds.includes(c.course_id)
      );
    },
    // Course yang belum ditambahkan ke editPathItems
    availableEditCourses() {
      const addedIds = this.editPathItems.map((i) => i.course_id);
      return this.courseFetchStore.courseCreatedList.filter(
        (c) => !addedIds.includes(c.course_id)
      );
    },
    filteredStudents() {
      const q = this.studentSearchQuery.toLowerCase();
      if (!q) return this.userStore.ActiveStudents;
      return this.userStore.ActiveStudents.filter((s) =>
        s.name?.toLowerCase().includes(q)
      );
    },
  },

  async mounted() {
    await this.userStore.fetchStudents();
    await this.learningPathStore.fetchPaths();
    this.courseFetchStore.fetchCourseByCreatorList();
  },

  methods: {
    // ---- Create Path ----
    openCreateModal() {
      this.newPath = { title: "", note: "" };
      this.newPathItems = [];
      this.selectedCourseId = "";
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    addItem() {
      if (!this.selectedCourseId) return;
      const course = this.courseFetchStore.courseCreatedList.find(
        (c) => c.course_id === this.selectedCourseId
      );
      if (!course) return;
      this.newPathItems.push({
        course_id: course.course_id,
        course_title: course.title,
        order: this.newPathItems.length + 1,
      });
      this.selectedCourseId = "";
    },
    removeItem(index) {
      this.newPathItems.splice(index, 1);
      // Re-assign urutan otomatis
      this.newPathItems.forEach((item, i) => (item.order = i + 1));
    },
    async submitCreatePath() {
      const payload = {
        title: this.newPath.title,
        note: this.newPath.note,
        items: this.newPathItems.map((i) => ({ course: i.course_id, order: i.order })),
      };
      const ok = await this.learningPathStore.createPath(payload);
      if (ok) this.closeCreateModal();
    },

    // ---- Edit Path ----
    openEditModal(path) {
      this.editingPathId = path.id;
      this.editPath = {
        title: path.title || "",
        note: path.note || "",
      };
      this.editPathItems = (path.items || []).map((item) => ({
        course_id: item.course,
        course_title: item.course_title,
        order: item.order,
      }));
      this.selectedEditCourseId = "";
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editingPathId = null;
      this.editPath = { title: "", note: "" };
      this.editPathItems = [];
      this.selectedEditCourseId = "";
    },
    addEditItem() {
      if (!this.selectedEditCourseId) return;
      const course = this.courseFetchStore.courseCreatedList.find(
        (c) => c.course_id === this.selectedEditCourseId
      );
      if (!course) return;
      this.editPathItems.push({
        course_id: course.course_id,
        course_title: course.title,
        order: this.editPathItems.length + 1,
      });
      this.selectedEditCourseId = "";
    },
    removeEditItem(index) {
      this.editPathItems.splice(index, 1);
      this.editPathItems.forEach((item, i) => (item.order = i + 1));
    },
    moveEditItemUp(index) {
      if (index <= 0) return;
      const temp = this.editPathItems[index];
      this.editPathItems[index] = this.editPathItems[index - 1];
      this.editPathItems[index - 1] = temp;
      this.editPathItems.forEach((item, i) => (item.order = i + 1));
    },
    moveEditItemDown(index) {
      if (index >= this.editPathItems.length - 1) return;
      const temp = this.editPathItems[index];
      this.editPathItems[index] = this.editPathItems[index + 1];
      this.editPathItems[index + 1] = temp;
      this.editPathItems.forEach((item, i) => (item.order = i + 1));
    },
    async submitEditPath() {
      const payload = {
        title: this.editPath.title,
        note: this.editPath.note,
        items: this.editPathItems.map((i) => ({ course: i.course_id, order: i.order })),
      };
      const ok = await this.learningPathStore.updatePath(this.editingPathId, payload);
      if (ok) this.closeEditModal();
    },

    // ---- Delete Path ----
    async deletePath(path) {
      const pathId = typeof path === "object" && path != null ? path.id : path;
      if (this.sharedStore.loading) return;

      const result = await Swal.fire({
        title: "Hapus Learning Path?",
        text: "Aksi ini tidak dapat dibatalkan.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, Hapus",
        cancelButtonText: "Batal",
      });
      if (result.isConfirmed) {
        await this.learningPathStore.deletePath(pathId);
      }
    },

    // ---- Assign Students ----
    openAssignModal(path) {
      this.selectedPath = path;
      this.selectedStudentIds = [];
      this.studentSearchQuery = "";
      this.showAssignModal = true;
    },
    closeAssignModal() {
      this.showAssignModal = false;
      this.selectedPath = null;
    },
    toggleStudent(studentId) {
      const idx = this.selectedStudentIds.indexOf(studentId);
      if (idx === -1) this.selectedStudentIds.push(studentId);
      else this.selectedStudentIds.splice(idx, 1);
    },
    async submitAssignStudents() {
      if (this.selectedStudentIds.length === 0) {
        Swal.fire("Info", "Pilih minimal 1 student.", "info");
        return;
      }
      const ok = await this.learningPathStore.assignStudents(
        this.selectedPath.id,
        this.selectedStudentIds
      );
      if (ok) this.closeAssignModal();
    },

    // ---- Remove / Un-assign Student ----
    async removeStudent(pathId, student) {
      const result = await Swal.fire({
        title: "Un-assign Student?",
        text: `Apakah Anda yakin ingin menghapus "${student.name}" dari learning path ini?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, Hapus",
        cancelButtonText: "Batal",
      });
      if (result.isConfirmed) {
        await this.learningPathStore.removeStudent(pathId, student.student_id);
      }
    },
  },
};
</script>


<template>
  <layouts-header></layouts-header>

  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Learning Path</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">Learning Path</li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <div class="content">
    <div class="container">
      <instructor-header></instructor-header>
      <div class="row">
        <instructor-sidebar></instructor-sidebar>

        <div class="col-lg-9">
          <!-- Page Title -->
          <div class="page-title d-flex align-items-center justify-content-between mb-3">
            <h5 class="fw-bold mb-0">Learning Paths</h5>
            <button class="btn btn-primary d-inline-flex align-items-center" @click="openCreateModal">
              <i class="isax isax-add-circle me-2"></i> Create New Path
            </button>
          </div>

          <!-- Empty State -->
          <div v-if="learningPathStore.learningPaths.length === 0 && !sharedStore.loading" class="text-center py-5">
            <i class="isax isax-path-square fs-1 text-muted d-block mb-3" style="font-size: 3rem !important;"></i>
            <h6 class="text-muted">Belum ada Learning Path</h6>
            <p class="text-muted fs-14">Klik "Create New Path" untuk membuat jalur pembelajaran pertamamu.</p>
          </div>

          <!-- Learning Path Cards -->
          <div
            v-for="path in learningPathStore.learningPaths"
            :key="path.id"
            class="card mb-3 border-0 shadow-sm"
          >
            <div class="card-body">
              <!-- Header -->
              <div class="d-flex align-items-start justify-content-between mb-3">
                <div>
                  <h6 class="fw-bold mb-1">{{ path.title }}</h6>
                  <p class="text-muted fs-13 mb-0" v-if="path.note">{{ path.note }}</p>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button
                    class="btn btn-sm btn-outline-warning d-inline-flex align-items-center"
                    title="Edit Learning Path"
                    @click="openEditModal(path)"
                  >
                    <i class="isax isax-edit-2 me-1"></i> Edit
                  </button>
                  <button
                    class="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
                    @click="openAssignModal(path)"
                  >
                    <i class="isax isax-profile-add me-1"></i> Assign Student
                    <span class="badge bg-primary ms-2">{{ path.student_count }}</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger d-inline-flex align-items-center"
                    :disabled="sharedStore.loading"
                    @click="deletePath(path)"
                  >
                    <i class="isax isax-trash"></i>
                  </button>
                </div>
              </div>

              <!-- Course Items -->
              <div class="border rounded-2 p-3">
                <p class="fw-medium fs-13 text-muted mb-2">
                  <i class="isax isax-book-1 me-1"></i>
                  Course dalam Path ({{ path.item_count }})
                </p>
                <div
                  v-for="item in path.items"
                  :key="item.id"
                  class="d-flex align-items-center py-2 border-bottom"
                >
                  <span class="badge bg-light text-dark me-3 fw-bold" style="min-width: 28px;">
                    {{ item.order }}
                  </span>
                  <router-link
                    :to="{ name: 'CourseDetail', params: { course_id: item.course } }"
                    class="fs-14 text-dark text-decoration-none fw-medium"
                    style="cursor: pointer;"
                  >
                    {{ item.course_title }}
                    <i class="isax isax-arrow-right-3 fs-12 ms-1 text-muted"></i>
                  </router-link>
                </div>
                <div v-if="path.items.length === 0" class="text-muted fs-13 text-center py-2">
                  Belum ada course ditambahkan.
                </div>
              </div>

              <!-- Enrolled Students -->
              <div class="mt-3" v-if="path.student_count > 0">
                <p class="fw-medium fs-13 text-muted mb-2">
                  <i class="isax isax-people me-1"></i>
                  Students yang Di-assign ({{ path.student_count }})
                </p>
                <div class="d-flex flex-wrap gap-2">
                  <span
                    v-for="student in (path.students || [])"
                    :key="student.student_id"
                    class="badge bg-light text-dark fs-12 fw-normal py-1 px-2 border d-inline-flex align-items-center gap-1"
                  >
                    <i class="isax isax-user fs-12 me-1"></i>
                    <span>{{ student.name }}</span>
                    <button
                      type="button"
                      class="btn p-0 border-0 ms-1 lh-1"
                      title="Un-assign student"
                      @click="removeStudent(path.id, student)"
                    >
                      <i class="fas fa-times text-danger" style="font-size: 11px;"></i>
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- /Learning Path Cards -->
        </div>
      </div>
    </div>
  </div>

  <!-- ======================== -->
  <!-- Modal: Create Path       -->
  <!-- ======================== -->
  <div
    class="modal fade"
    :class="{ show: showCreateModal }"
    :style="{ display: showCreateModal ? 'block' : 'none' }"
    tabindex="-1"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title fw-bold">Create Learning Path</h5>
          <button type="button" class="btn-close" @click="closeCreateModal"></button>
        </div>
        <div class="modal-body">
          <!-- Title & Note -->
          <div class="mb-3">
            <label class="form-label fw-medium">
              Path Title <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              placeholder="e.g. Path Dasar Web Development"
              v-model="newPath.title"
            />
          </div>
          <div class="mb-3">
            <label class="form-label fw-medium">Note / Deskripsi</label>
            <textarea
              class="form-control"
              rows="2"
              placeholder="Tuliskan deskripsi singkat tentang path ini..."
              v-model="newPath.note"
            ></textarea>
          </div>

          <hr />

          <!-- Add Course Items -->
          <label class="form-label fw-medium">Tambah Course ke Path</label>
          <div class="d-flex gap-2 mb-3">
            <select class="form-select" v-model="selectedCourseId">
              <option value="" disabled>-- Pilih Course --</option>
              <option
                v-for="course in availableCourses"
                :key="course.course_id"
                :value="course.course_id"
              >
                {{ course.title }}
              </option>
            </select>
            <button
              class="btn btn-outline-primary flex-shrink-0"
              :disabled="!selectedCourseId"
              @click="addItem"
            >
              <i class="isax isax-add-circle me-1"></i> Add
            </button>
          </div>

          <!-- Item List -->
          <div class="border rounded-2 p-3" v-if="newPathItems.length > 0">
            <p class="fw-medium fs-13 text-muted mb-2">
              <i class="isax isax-book-1 me-1"></i> Course yang Ditambahkan ({{ newPathItems.length }})
            </p>
            <div
              v-for="(item, index) in newPathItems"
              :key="index"
              class="d-flex align-items-center justify-content-between py-2 border-bottom"
            >
              <div class="d-flex align-items-center">
                <span class="badge bg-primary me-3 fw-bold" style="min-width: 28px;">
                  {{ item.order }}
                </span>
                <span class="fs-14">{{ item.course_title }}</span>
              </div>
              <button
                class="btn btn-sm btn-outline-danger py-0"
                @click="removeItem(index)"
              >
                <i class="isax isax-trash fs-12"></i>
              </button>
            </div>
          </div>
          <div v-else class="text-center text-muted fs-13 py-2 border rounded-2">
            Belum ada course ditambahkan. (Opsional)
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" @click="closeCreateModal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary d-inline-flex align-items-center"
            :disabled="!newPath.title.trim()"
            @click="submitCreatePath"
          >
            <i class="isax isax-add-circle me-2"></i> Create Path
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="showCreateModal"></div>

  <!-- ======================== -->
  <!-- Modal: Edit Path         -->
  <!-- ======================== -->
  <div
    class="modal fade"
    :class="{ show: showEditModal }"
    :style="{ display: showEditModal ? 'block' : 'none' }"
    tabindex="-1"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title fw-bold">Edit Learning Path</h5>
          <button type="button" class="btn-close" @click="closeEditModal"></button>
        </div>
        <div class="modal-body">
          <!-- Title & Note -->
          <div class="mb-3">
            <label class="form-label fw-medium">
              Path Title <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              placeholder="e.g. Path Dasar Web Development"
              v-model="editPath.title"
            />
          </div>
          <div class="mb-3">
            <label class="form-label fw-medium">Note / Deskripsi</label>
            <textarea
              class="form-control"
              rows="2"
              placeholder="Tuliskan deskripsi singkat tentang path ini..."
              v-model="editPath.note"
            ></textarea>
          </div>

          <hr />

          <!-- Add Course Items -->
          <label class="form-label fw-medium">Tambah Course ke Path</label>
          <div class="d-flex gap-2 mb-3">
            <select class="form-select" v-model="selectedEditCourseId">
              <option value="" disabled>-- Pilih Course --</option>
              <option
                v-for="course in availableEditCourses"
                :key="course.course_id"
                :value="course.course_id"
              >
                {{ course.title }}
              </option>
            </select>
            <button
              class="btn btn-outline-primary flex-shrink-0"
              :disabled="!selectedEditCourseId"
              @click="addEditItem"
            >
              <i class="isax isax-add-circle me-1"></i> Add
            </button>
          </div>

          <!-- Item List -->
          <div class="border rounded-2 p-3" v-if="editPathItems.length > 0">
            <p class="fw-medium fs-13 text-muted mb-2">
              <i class="isax isax-book-1 me-1"></i> Course dalam Path ({{ editPathItems.length }})
            </p>
            <div
              v-for="(item, index) in editPathItems"
              :key="index"
              class="d-flex align-items-center justify-content-between py-2 border-bottom"
            >
              <div class="d-flex align-items-center">
                <span class="badge bg-primary me-3 fw-bold" style="min-width: 28px;">
                  {{ item.order }}
                </span>
                <span class="fs-14">{{ item.course_title }}</span>
              </div>
              <div class="d-flex align-items-center gap-1">
                <button
                  type="button"
                  class="btn btn-sm btn-light py-0 px-2"
                  :disabled="index === 0"
                  title="Pindah ke atas"
                  @click="moveEditItemUp(index)"
                >
                  <i class="fas fa-arrow-up fs-12"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-light py-0 px-2"
                  :disabled="index === editPathItems.length - 1"
                  title="Pindah ke bawah"
                  @click="moveEditItemDown(index)"
                >
                  <i class="fas fa-arrow-down fs-12"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger py-0 ms-1"
                  title="Hapus dari path"
                  @click="removeEditItem(index)"
                >
                  <i class="isax isax-trash fs-12"></i>
                </button>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-muted fs-13 py-2 border rounded-2">
            Belum ada course ditambahkan. (Opsional)
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" @click="closeEditModal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary d-inline-flex align-items-center"
            :disabled="!editPath.title.trim()"
            @click="submitEditPath"
          >
            <i class="isax isax-tick-circle me-2"></i> Simpan Perubahan
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="showEditModal"></div>

  <!-- ======================== -->
  <!-- Modal: Assign Students   -->
  <!-- ======================== -->
  <div
    class="modal fade"
    :class="{ show: showAssignModal }"
    :style="{ display: showAssignModal ? 'block' : 'none' }"
    tabindex="-1"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title fw-bold mb-0">Assign Students</h5>
            <p class="text-muted fs-13 mb-0" v-if="selectedPath">
              Path: <strong>{{ selectedPath.title }}</strong>
            </p>
          </div>
          <button type="button" class="btn-close" @click="closeAssignModal"></button>
        </div>
        <div class="modal-body">
          <!-- Search -->
          <div class="input-icon mb-3">
            <span class="input-icon-addon">
              <i class="isax isax-search-normal-14"></i>
            </span>
            <input
              type="text"
              class="form-control"
              placeholder="Cari nama student..."
              v-model="studentSearchQuery"
            />
          </div>

          <!-- Student List -->
          <div class="border rounded-2" style="max-height: 350px; overflow-y: auto;">
            <div
              v-for="student in filteredStudents"
              :key="student.userID"
              class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
              :class="{ 'bg-primary-subtle': selectedStudentIds.includes(student.userID) }"
              style="cursor: pointer;"
              @click="toggleStudent(student.userID)"
            >
              <div class="d-flex align-items-center">
                <div
                  class="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                  style="width: 36px; height: 36px; background: #e8eeff;"
                >
                  <i class="isax isax-user text-primary"></i>
                </div>
                <p class="mb-0 fw-medium fs-14">{{ student.name }}</p>
              </div>
              <span
                v-if="selectedStudentIds.includes(student.userID)"
                class="badge bg-primary"
              >
                <i class="fas fa-check me-1"></i> Selected
              </span>
              <span v-else class="badge bg-light text-muted border">+ Add</span>
            </div>
            <div v-if="filteredStudents.length === 0" class="text-center text-muted py-4 fs-14">
              Tidak ada student ditemukan.
            </div>
          </div>

          <!-- Summary -->
          <div class="mt-3 d-flex align-items-center justify-content-between">
            <p class="mb-0 fs-13 text-muted">
              <strong>{{ selectedStudentIds.length }}</strong> student dipilih
            </p>
            <button
              class="btn btn-sm btn-outline-secondary fs-12"
              @click="selectedStudentIds = []"
            >
              Clear Selection
            </button>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" @click="closeAssignModal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary d-inline-flex align-items-center"
            @click="submitAssignStudents"
          >
            <i class="isax isax-profile-add me-2"></i> Save Assignment
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="showAssignModal"></div>

  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
</template>